import { ReasonToBuyUI } from "./ReasonToBuyUI";
import { StandardProps } from "../componentMap";
import { useVariant } from "@/hooks/useVariant/useVariant";
import { TickCircleOutlined } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/TickCircleOutlined";
import { DeliveryOutlined } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/DeliveryOutlined";
import { GiftCardOutlined } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/GiftCardOutlined";
import { GuaranteesAndTroubleshootingOutlined } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/GuaranteesAndTroubleshootingOutlined";
import { pricingGroups } from "@/utils/product/getPromoMessaging";
import {
  EXCLUDED_CATEGORIES,
  EXCLUDED_PRODUCT_ID,
  EXCLUDED_PRODUCT_TYPE,
  EXCLUDED_BRANDS,
} from "@/utils/pricePromise/PricePromiseExclusions.ts";
import { ProtectPlusSvg } from "../PostAddToBasketDrawer/components/ProtectPlusCompliance/ProtectPlusSvg";
import { getScheme } from "../PostAddToBasketDrawer/scheme/schemeDecisioner";
import { replaceVariables } from "../PostAddToBasketDrawer/utils";
import { formatTimeToAmPm } from "@/utils/date/formatTime";
import { ReactNode, useMemo, useState } from "react";
import { PricePromiseDrawer } from "../PricePromise/PricePromiseDrawer";
import { WaitroseLogoSvg } from "../ClickAndCollectCountdown/utils/WaitroseLogoSvg";
import { DMS_IDS } from "@/constants/constants";
import {
  isProtectPlusExtendedWarranty,
  isIncludedGuarantee,
} from "./utils/utils";
import { sendEvent } from "@JohnLewisPartnership/experiments-utils";

export type Card = {
  id: string;
  title: string;
  description?: string | undefined;
  descriptionHref?: string | undefined;
  onDescriptionClick?: () => void;
  icon?: ReactNode;
};

type CardProps = Card[];

type AvailableOption = {
  aliases?: { dmsId?: string | null } | null;
  earliestFulfilment?: {
    orderByLocalTimeToday?: string | null;
  } | null;
};

function buildClickAndCollectCard(
  option: AvailableOption | undefined,
): Card | null {
  if (!option) return null;

  const orderByLocalTimeToday =
    option.earliestFulfilment?.orderByLocalTimeToday;

  if (orderByLocalTimeToday) {
    const [h, m] = orderByLocalTimeToday.split(":").map((t) => parseInt(t, 10));
    const cutoffTime = new Date();
    cutoffTime.setHours(h || 0, m || 0, 0, 0);
    const timeLeftMs = cutoffTime.getTime() - Date.now();

    if (timeLeftMs > 0) {
      // IN HOURS: before cutoff (includes early morning before pre-countdown window)
      return {
        id: "clickandcollect",
        title: "Get it tomorrow",
        description: `Order before ${formatTimeToAmPm(orderByLocalTimeToday)} and collect from your nearest Waitrose`,
        icon: <WaitroseLogoSvg />,
      };
    }
  }

  // OUT OF HOURS: after cutoff, or no earliestFulfilment data (e.g. Saturday)
  return {
    id: "clickandcollect",
    title: "Collect from your nearest Waitrose",
    icon: <WaitroseLogoSvg />,
  };
}

export function ReasonToBuy({ product }: Pick<StandardProps, "product">) {
  const { variant } = useVariant(product);
  const protectPlusScheme = getScheme(product);
  const [isPricePromiseDrawerOpen, setIsPricePromiseDrawerOpen] =
    useState(false);

  const isJohnLewisProduct =
    typeof product?.brand?.name === "string" &&
    ["john lewis", "anyday", "and/or", "kin"].some((brand) =>
      product?.brand?.name.toLowerCase().includes(brand),
    );

  const includedGuarantee = variant.services?.find(isIncludedGuarantee);
  const protectPlusItem =
    variant.services?.filter(isProtectPlusExtendedWarranty)?.[0] ?? null;

  const isPricePromiseEligible = useMemo(() => {
    const brandName = product?.brand?.name?.toLowerCase();

    const hasExclusiveAdditional = product?.messaging?.some(
      (msg) =>
        msg.type === "additional" &&
        typeof msg.title === "string" &&
        msg.title.toLowerCase().includes("exclusive"),
    );
    const productGroupHasPromos = product?.messaging?.some(
      (msg) =>
        "promotionGroup" in msg && pricingGroups.includes(msg.promotionGroup),
    );
    const categoryIsExcluded = product?.browsePageHierarchy?.some((page) =>
      EXCLUDED_CATEGORIES.includes(page.categoryId),
    );
    const brandIsExcluded =
      typeof brandName === "string" &&
      EXCLUDED_BRANDS.some((b) => brandName.includes(b));

    return !(
      hasExclusiveAdditional ||
      categoryIsExcluded ||
      brandIsExcluded ||
      product.aliases?.pimProductId === EXCLUDED_PRODUCT_ID ||
      product.__typename === EXCLUDED_PRODUCT_TYPE ||
      productGroupHasPromos
    );
  }, [product]);

  const clickAndCollectCard = useMemo(() => {
    const cncOption = product.fulfilmentOptions?.available?.find(
      (option) => option.aliases?.dmsId === DMS_IDS.ClickAndCollect,
    );
    return buildClickAndCollectCard(cncOption);
  }, [product]);

  const protectPlusCard = useMemo(() => {
    if (!protectPlusItem) return null;

    const templateVars = {
      VAR_YEARS: protectPlusItem.duration ?? "",
      VAR_PRICE: protectPlusItem.price?.display ?? "",
    };

    const highlight = replaceVariables(
      protectPlusScheme?.schemeHighlight || "",
      templateVars,
    );
    const marketingMessage = replaceVariables(
      protectPlusScheme?.schemeMarketingMessage || "",
      templateVars,
    );

    return {
      id: "protect-plus",
      title:
        highlight ||
        `Add ${templateVars.VAR_YEARS} years of cover for accidental damage: ${templateVars.VAR_PRICE}`,
      description:
        marketingMessage ||
        `Protect your item from accidental damage for ${templateVars.VAR_YEARS} years, in addition to your product's standard guarantee.`,
      icon: <ProtectPlusSvg />,
    };
  }, [protectPlusItem, protectPlusScheme]);

  const nextDayDeliveryOption = product.fulfilmentOptions?.available.find(
    (option) => option.aliases.dmsId === DMS_IDS.NextOrNamedDayDelivery,
  );
  const formattedOrderByTime = formatTimeToAmPm(
    nextDayDeliveryOption?.earliestFulfilment?.orderByLocalTimeToday,
  );

  const cards: CardProps = [
    clickAndCollectCard,
    includedGuarantee && {
      id: "guarantee",
      title: includedGuarantee.title,
      description: "Included with your purchase",
      icon: <GuaranteesAndTroubleshootingOutlined size="large" />,
    },
    {
      id: "delivery",
      title: "Free standard delivery over £50",
      description: "Otherwise £4.50",
      icon: <DeliveryOutlined size="large" />,
    },
    {
      id: "split-the-cost",
      title: "Split the cost",
      description: "Flexible payments available",
      icon: <GiftCardOutlined size="large" />,
    },
    isPricePromiseEligible && {
      id: "price-promise",
      title: "Our price promise",
      description: "Confidence in every purchase",
      onDescriptionClick: () => {
        setIsPricePromiseDrawerOpen(true);
        sendEvent("WPDP | Price Promise Info Displayed");
      },
      icon: <TickCircleOutlined size="large" />,
    },
    protectPlusCard,
  ].filter((card): card is Card => !!card);

  if (formattedOrderByTime && nextDayDeliveryOption) {
    cards.push({
      id: "next-day-delivery",
      title: "Next day delivery available",
      description: `Order by ${formattedOrderByTime} to receive by the next day. ${nextDayDeliveryOption.price.display}`,
      icon: <DeliveryOutlined size="large" />,
    });
  }

  if (!cards.length) return null;

  return (
    <>
      <ReasonToBuyUI cards={cards} />
      {isPricePromiseEligible && (
        <PricePromiseDrawer
          product={product}
          isOpen={isPricePromiseDrawerOpen}
          onClose={() => setIsPricePromiseDrawerOpen(false)}
          isJohnLewisProduct={isJohnLewisProduct}
        />
      )}
    </>
  );
}
